import { server } from "../../server";
import { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

function MedicalAlert() {
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  const todayDate = new Date().toISOString().split("T")[0];
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const MySwal = withReactContent(Swal);

  const [formData, setFormData] = useState({
    admission_no: "",
    category: "",
    remark: "",
    medicine: "",
    condition: "",
    callParents: false,
  });

  const resetForm = () => {
    setFormData({
      admission_no: "",
      category: "",
      remark: "",
      medicine: "",
      condition: "",
      callParents: false,
    });
    setIsEditing(false);
    setEditId(null);
  };

  const handleEdit = (alert) => {
    setFormData({
      admission_no: alert.admission_no,
      category: alert.category,
      remark: alert.remark,
      medicine: (alert.medicine || []).join(", "),
      condition: alert.condition,
      callParents: alert.callParents,
    });
    setIsEditing(true);
    setEditId(alert.id);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditing) {
        await axios.put(
          `${server}/UpdateMedicalAlert`,
          {
            id: editId,
            category: formData.category,
            remark: formData.remark,
            condition: formData.condition,
            callParents: formData.callParents,
            medicine: formData.medicine.split(",").map((m) => m.trim()),
          },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );
        toast.success("Medical Alert Updated");
      } else {
        await axios.post(
          `${server}/CreateMedicalAlert`,
          {
            ...formData,
            medicine: formData.medicine.split(",").map((m) => m.trim()),
          },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );
        toast.success("Medical Alert Created");
      }

      document.querySelector('[data-bs-dismiss="modal"]')?.click();

      fetchAlerts();
      resetForm();
    } catch (err) {
      toast.error("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${server}/GetAllMedicalAlert`, {
        headers: {
          Authorization: `${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      setAlerts(res.data || []);
    } catch (err) {
      toast.error("Failed to fetch alerts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, []);

  // Filter
  const filteredRecords = alerts.filter((scholarship) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(scholarship).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(searchLower);
      } else if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (subValue) =>
            typeof subValue === "string" &&
            subValue.toLowerCase().includes(searchLower),
        );
      }
      return false;
    });
  });

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleDelete = async (id) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: "You won’t be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${server}/DeleteMedicalAlert?id=${id}`);
        toast.success("Medical Alert Deleted");
        fetchAlerts();
      } catch (err) {
        toast.error("Failed to delete alert");
      }
    }
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div
            class="modal fade gd-example-modal-lg"
            tabindex="-1"
            role="dialog"
            aria-labelledby="myLargeModalLabel"
            aria-hidden="true"
          >
            <div class="modal-dialog modal-lg">
              <div class="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header d-flex justify-content-between">
                    <h5
                      className="modal-title fs-4"
                      id="exampleModalToggleLabel"
                    >
                      Add Medical Alert
                    </h5>
                    <div className="d-flex align-items-center">
                      <span className="badge bg-success-soft me-3 px-3">
                        Editable
                      </span>
                      <div className="form-check form-switch me-4">
                        <input
                          type="checkbox"
                          className="form-check-input m-0"
                          checked
                          readOnly
                        />
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      ></button>
                    </div>
                  </div>

                  <div className="modal-body">
                    <div className="row g-2 mb-2">
                      {/* Admission No */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Admission No <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="admission_no"
                          value={formData.admission_no}
                          onChange={handleChange}
                          placeholder="Enter Admission No"
                          required
                        />
                      </div>

                      {/* Category */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Category <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          placeholder="Enter Category"
                          required
                        />
                      </div>

                      {/* Condition */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Condition <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="condition"
                          value={formData.condition}
                          onChange={handleChange}
                          placeholder="Enter Condition (e.g. Critical)"
                          required
                        />
                      </div>

                      {/* Remark */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">Remark</label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="remark"
                          value={formData.remark}
                          onChange={handleChange}
                          placeholder="Enter Remark"
                        />
                      </div>

                      {/* Medicine */}
                      <div className="col-lg-12 col-12">
                        <label className="form-label mb-1 ms-3">Medicine</label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="medicine"
                          value={formData.medicine}
                          onChange={handleChange}
                          placeholder="Enter Medicines (comma separated)"
                        />
                      </div>

                      {/* Call Parents */}
                      <div className="col-lg-12 col-12 mt-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="callParents"
                            checked={formData.callParents}
                            onChange={handleChange}
                          />
                          <label className="form-check-label">
                            Call Parents
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer d-flex justify-content-between py-2">
                    <div>
                      <button
                        type="button"
                        className="btn btn-secondary px-3 me-1 rounded-pill"
                        data-bs-dismiss="modal"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                      <button
                        type="submit"
                        class="btn btn-primary px-5 rounded-pill fw-normal"
                        disabled={loading}
                      >
                        {loading ? (
                          <div
                            class="spinner-border spinner-border-sm"
                            role="status"
                          ></div>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((medicalalert, index) => (
                          <tr>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span class="text-dark">Admission No</span>{" "}
                              &mdash; {medicalalert.admission_no}
                              <br />
                              <span class="text-dark">
                                Category
                              </span> &mdash; {medicalalert.category}
                              <br />
                              <span class="text-dark">Remark</span> &mdash;{" "}
                              {medicalalert.remark}
                              <br />
                              <span class="text-dark">
                                Medicine
                              </span> &mdash; {medicalalert.medicine}
                              <br />
                              <span class="text-dark">
                                Condition
                              </span> &mdash; {medicalalert.condition}
                              <br />
                              <span class="text-dark">Call Parents</span>{" "}
                              &mdash; {medicalalert.callParents ? "Yes" : "No"}
                              <br />
                              <span class="text-dark">Date</span> &mdash;{" "}
                              <span className="text-uppercase">
                                {medicalalert.createdAt
                                  ? new Date(
                                      medicalalert.createdAt,
                                    ).toLocaleString("en-GB", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    })
                                  : ""}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <span
                                  onClick={() => handleEdit(medicalalert)}
                                  class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Update
                                </span>
                                <span
                                  onClick={() => handleDelete(medicalalert._id)}
                                  class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Delete
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="d-flex bg-white justify-content-between rounded-4">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                        <Link
                          data-bs-toggle="modal"
                          onClick={resetForm}
                          data-bs-target=".gd-example-modal-lg"
                          class="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                        >
                          Add Medical Alert
                        </Link>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Front Office</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Medical Alert
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Medical Alert
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              0.2 MB Size
                            </span>
                          </p>
                        </div>
                        <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                  Medical Alert
                                </span>
                                <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                  Category Wise Medical Alert
                                </span>
                                <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                  Called Parents
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default MedicalAlert;
